// Entry point - imports and initializes all modules
import { game, store } from "../state/index.ts";
import * as config from "../legacy/config.js";
import * as board from "../legacy/board.js";
import * as engine from "../legacy/engine.js";
import * as book from "../legacy/book.js";
import * as repertoire from "../legacy/repertoire.js";
import * as lines from "../legacy/lines.js";
import * as quickStatus from "../legacy/quickStatus.js";
import * as toast from "../legacy/toast.js";
import * as boardNav from "../legacy/boardNav.js";
import * as practice from "../legacy/practice.js";
import * as practiceView from "../legacy/practiceView.js";
import { setupPage, resolveSession } from "./shell.ts";
import { RoutineBar } from "../components/routine/RoutineBar.tsx";
import { Board } from "../components/board/Board.tsx";
import { LivePanel, EvalBar, MoveTape, restoreLiveSettings } from "../components/study/LivePanel.tsx";
import { PracticeActions, TakeBack } from "../components/practice/PracticeActions.tsx";
import * as track from "../services/track.ts";
import { onPositionChanged } from "../legacy/positionOrchestrator.js";

// DOM elements
const elements = {
  // Form and controls

  // Config

  // How It Works

  // My Games

  // Lines (My Lines dropdown)
  lineSelector: document.getElementById("lineSelector"),

  // Add Line dropdown

  // Line Board (mini board for creating lines)

  // Repertoire

  // Engine
  engineVersionBadge: document.getElementById("engineVersionBadge"),
  engineStatus: document.getElementById("engineStatus"),
  engineLines: document.getElementById("engineLines"),
  engineBest: document.getElementById("engineBest"),

  // Board
  boardContainer: document.querySelector(".board-container"),
  boardResizeHandle: document.getElementById("boardResizeHandle"),
  boardSideSelect: document.getElementById("boardSide"),
  autoPlayMode: document.getElementById("autoPlayMode"),

  // Practice panels
  linePickerDropdown: document.getElementById("linePickerDropdown"),
  linePickerTriggers: document.querySelectorAll(".linepicker-trigger"),
  linePickerClose: document.querySelector(".linepicker-close"),


  // Book moves
  bookMovesList: document.getElementById("bookMoves"),
  myMovesList: document.getElementById("myMoves"),
  userTurnMessage: document.getElementById("userTurnMessage"),
  userLevelMovesColorSpan: document.getElementById("userLevelMovesColor"),

  // Repertoire
  repertoireDetails: document.getElementById("repertoireDetails"),
  repertoireSummary: document.getElementById("repertoireSummary"),
  repertoireNextMovesEl: document.getElementById("repertoireNextMoves"),

  // Quick Status Bar
  quickSparringValue: document.getElementById("quickSparringValue"),
  quickSparringBar: document.getElementById("quickSparringBar"),
  quickSparringDetail: document.getElementById("quickSparringDetail"),
  quickMyGamesValue: document.getElementById("quickMyGamesValue"),
  quickMyGamesBar: document.getElementById("quickMyGamesBar"),
  quickMyGamesTotal: document.getElementById("quickMyGamesTotal"),
  quickMyGamesDetail: document.getElementById("quickMyGamesDetail"),
  quickRepertoireValue: document.getElementById("quickRepertoireValue"),
  quickEngineValue: document.getElementById("quickEngineValue"),
  quickEngineDepth: document.getElementById("quickEngineDepth"),
  quickEngineCog: document.getElementById("quickEngineCog"),

  // Player config (My Games)
  playerConfigDetails: document.getElementById("playerConfigDetails"),
  playerConfigSummary: document.getElementById("playerConfigSummary"),

  // Debug

  // Lichess download

  // Game imports list

  // Auth
  navUserName: document.getElementById("navUserName"),
  logoutBtn: document.getElementById("logoutBtn"),
  googleLoginBtn: document.getElementById("googleLoginBtn"),
  userDropdownWrapper: document.getElementById("userDropdownWrapper"),

  // Toast
  toastContainer: document.getElementById("toastContainer"),

  // Board nav
  navFirst: document.getElementById("navFirst"),
  navLast: document.getElementById("navLast"),
  navStatus: document.getElementById("navStatus"),

  // Onboarding
};

// True when the move just played belongs to the practice, which then drives the sparring reply itself
let practiceHandledMove = false;

// Initialize modules
config.initConfig(elements, {
  onBoardOrientationChange: () => {},
});

board.initBoard(elements, {
  onUserMove: handleUserMove,
  onEvaluatePosition: () => engine.evaluatePosition(),
  onCheckRepertoire: () => repertoire.checkRepertoire(),
  onLoadBook: () => {
    onPositionChanged();
  },
  onAfterUserMove: (move) => {
    practiceHandledMove = practice.onUserMove(move);
  },
});

engine.initEngine(elements, {
  onEngineScoreChanged: () => repertoire.refreshRepertoireEngineHint(),
});

book.initBook(elements, {
  onEvaluatePosition: () => engine.evaluatePosition(),
  onSparringMove: (move) => {
    boardNav.onMoveMade();
    practice.onSparringMove(move);
  },
});

repertoire.initRepertoire(elements);

// The list itself lives on /lines now; this page only reads it, for the line picker.
lines.initLines({}, {
  onLinesChanged: () => practiceView.renderLinePicker(),
});

boardNav.initBoardNav(elements, {
  // "Restart" starts the practice over; without a practice on the board it falls back to reloading the position
  onRestart: () => {
    if (!store.get('practice.line')) return false;
    practiceView.restartPractice();
    return true;
  },
});

practiceView.initPracticeView({
  closePanels: () => closeAllDropdowns(),
});

quickStatus.initQuickStatus(elements);


// Dropdown management - only one dropdown open at a time

const dropdowns = [
  { dropdown: elements.linePickerDropdown, triggers: elements.linePickerTriggers, close: elements.linePickerClose },
];

function isDropdownPinned(dropdown) {
  return dropdown?.classList.contains("pinned");
}

function closeAllDropdowns(force = false) {
  dropdowns.forEach(({ dropdown, triggers }) => {
    // Skip pinned dropdowns unless force closing
    if (!force && isDropdownPinned(dropdown)) {
      return;
    }
    dropdown?.classList.remove("open");
    dropdown?.classList.remove("pinned");
    triggers.forEach((trigger) => trigger.classList.remove("active"));
  });
}

function closeDropdown(dropdown, triggers) {
  dropdown?.classList.remove("open");
  dropdown?.classList.remove("pinned");
  triggers?.forEach((trigger) => trigger.classList.remove("active"));
}

function toggleDropdown(targetDropdown, targetTriggers) {
  const isOpen = targetDropdown?.classList.contains("open");
  closeAllDropdowns();
  if (!isOpen) {
    targetDropdown?.classList.add("open");
    targetTriggers.forEach((trigger) => trigger.classList.add("active"));
  }
}

function toggleDropdownPin(dropdown) {
  if (!dropdown) return;
  dropdown.classList.toggle("pinned");
}

// Suggestions from the user's games read the opening of every game of the last year: the request goes out
// when the picker is opened, not on load, since nothing on the page shows them until then.
elements.linePickerTriggers.forEach((trigger) => {
  trigger.addEventListener("click", () => practiceView.loadSuggestedLines());
});

// Wire up triggers, close buttons, and pin buttons for each dropdown
dropdowns.forEach(({ dropdown, triggers, close }) => {
  triggers.forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();
      toggleDropdown(dropdown, triggers);
    });
  });

  // Header close button
  close?.addEventListener("click", () => closeDropdown(dropdown, triggers));

  // Pin button
  const pinBtn = dropdown?.querySelector(".dropdown-pin-btn");
  pinBtn?.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleDropdownPin(dropdown);
  });

  // Footer close buttons
  const footerCloseBtns = dropdown?.querySelectorAll(".dropdown-close-btn");
  footerCloseBtns?.forEach((btn) => {
    btn.addEventListener("click", () => closeDropdown(dropdown, triggers));
  });
});

// Close dropdown on outside click (respects pinned state)
document.addEventListener("click", (e) => {
  const openDropdown = dropdowns.find(({ dropdown }) => dropdown?.classList.contains("open"));
  if (openDropdown) {
    // Don't close if pinned
    if (isDropdownPinned(openDropdown.dropdown)) {
      return;
    }
    const clickedOnTrigger = dropdowns.some(({ triggers }) =>
      Array.from(triggers).some((trigger) => trigger.contains(e.target))
    );
    const clickedInDropdown = dropdowns.some(({ dropdown }) => dropdown?.contains(e.target));
    if (!clickedInDropdown && !clickedOnTrigger) {
      closeAllDropdowns();
    }
  }
});

// Config mini dropdown
// Closing the panels on Escape is this page's business; the account menu is the shell's.
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeAllDropdowns();
});

// Collapsible config columns
document.querySelectorAll(".config-column--collapsible .config-column__toggle").forEach((toggle) => {
  toggle.addEventListener("click", () => {
    const column = toggle.closest(".config-column--collapsible");
    column?.classList.toggle("collapsed");
  });
});


elements.autoPlayMode?.addEventListener("change", () => {
  config.persistAutoPlay();
  config.persistUserConfig();
  practice.onAutoPlayChanged();
});

elements.boardSideSelect?.addEventListener("change", () => {
  store.set('board.userSide', elements.boardSideSelect.value);
  config.updateBoardOrientation();
  config.saveConfig();
});

// Leaving mid-practice: remember that it happened, without counting it as a practice
window.addEventListener("pagehide", () => practice.abandon());

// Handle user moves
function handleUserMove({ from, to, isGameOver }) {
  boardNav.onMoveMade();
  if (practiceHandledMove) {
    // The practice answers on its own clock; the panels just follow the new position
    practiceHandledMove = false;
    onPositionChanged();
    return;
  }
  if (!isGameOver) {
    const autoPlayEnabled = config.isAutoPlayEnabled();
    const userBoardSide = config.getSelectedBoardSide()[0];
    const autoplayTurn = game.turn() !== userBoardSide;
    setTimeout(() => onPositionChanged({ autoPlay: autoPlayEnabled && autoplayTurn }), 300);
  } else {
    engine.evaluatePosition();
  }
}

// Initialize application
// The engine's switches as this device left them, before the engine is first asked anything
restoreLiveSettings();

// The line's menu (the "Line" button of the header) closes on a click outside it, and once one of its actions is used
document.addEventListener("click", (event) => {
  const menu = document.querySelector(".study-menu");
  if (!menu?.open) return;
  const target = event.target;
  if (!menu.contains(target) || target.closest(".study-menu__actions .btn")) menu.open = false;
});

(async function init() {
  // 0. Toasts, the account menu and this page's components, before anything reads the DOM
  setupPage({
    islands: {
      board: Board,
      "eval-bar": EvalBar,
      "study-live": LivePanel,
      "move-tape": MoveTape,
      "practice-actions": PracticeActions,
      "take-back": TakeBack,
      "routine-bar": RoutineBar,
    },
    onConfigLoaded: (serverConfig) => {
      config.applyUserConfiguration(serverConfig, {
        // Needs a session, so it waits for the configuration that proves there is one
        onApplied: () => book.loadMyMovesForCurrentPosition(),
      });
    },
    onAuthStateChanged: (isLoggedIn) => {
      repertoire.setLoggedIn(isLoggedIn);
      lines.setLoggedIn(isLoggedIn);
    },
  });

  // 1. Restore UI state from localStorage (no API calls)
  config.restoreCollapseState();
  repertoire.restoreRepertoireCollapseState();
  config.restoreAutoPlay();
  config.loadStoredConfig();
  config.updateConfigSummary();
  repertoire.updateRepertoireSummary();
  board.syncBoard();
  store.set('progress.anonymousLimit', Number(document.body.dataset.anonymousLimit ?? 2));
  track.visit();

  // 2. Check auth and load user config FIRST (before any data loads)
  // This will trigger onAuthStateChanged which loads repertoires
  await resolveSession();
  if (store.get('user.currentUser')) track.identify();

  // 3. Put a line on the board. It comes after the auth check on purpose: the server configuration
  // brings its own board side, and the line's colour has to win.
  const opening = await practice.bootstrap({ tool: document.body.dataset.tool || null });
  // Without a line there is still a position on the board, and the panels describe it. The
  // dashboard shows no board, so nothing is asked for until a practice starts.
  if (opening === "empty") {
    onPositionChanged();
  }
  practiceView.renderLinePicker();
})();
